import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import "./style.css";
import sizeCarta from "./assets/Carta.png"
import sizeDobleCarta from "./assets/dobleCarta.png"
import sizeArchd from "./assets/archD.png"
import iconVisible from "./assets/ico-visible.svg";
import iconHidden from "./assets/ico-hidden.svg";

import { useSelector, useDispatch } from 'react-redux';
import {
    setPageSize,
    setTemplatePageOption,
    setTemplateFooterOption,
    setTemplateAnchor,
    setTemplateFooterText,
    updateTemplateBlock,
    duplicateTemplateBlock,
    removeTemplateBlock,
    reorderTemplateBlocks,
    selectTemplateBlock,
    activateBoxTool,
    activateStraightLineTool,
    setStraightLineToolColor,
    setStraightLineToolStrokeWidth,
    activateSymbolDrawerTool,
    activateTemplateTextTool,
    activateTemplateImageTool,
    clearSpawnPreview,
    setHandlesHidden,
    TEMPLATE_IMAGE_RATIO_DEFAULT,
} from '../../../../redux/reducers/global/view/viewSlice';
import {
    setNodeAttributes,
    setNodesActionCreator,
} from '../../../../redux/reducers/global/node/nodeSlice';
import { PAGE_SIZE_A4, PAGE_SIZE_A3, PAGE_SIZE_ARCH_D } from '../../../../utilities/constants/pageSizeConstants';
import { uploadEditorTemplateMedia } from '../../../../api/editorTemplates';

import Symbols from '../../../../utilities/mappings/symbols/Symbols';
import { prepareDragPayload, DEFAULT_ICON_SCALE_CM } from '../../../../utilities/dragHelpers';
import sanitizeHtml from 'sanitize-html';

const IMAGE_RATIO_OPTIONS = [
    { value: 'square', label: 'Cuadrada' },
    { value: 'rectangle', label: 'Rectangular' },
];
const IMAGE_FILE_ACCEPT = 'image/png,image/jpeg,image/webp,image/svg+xml,.svg';
const IMAGE_FILE_MAX_BYTES = 2 * 1024 * 1024;
const IMAGE_FILE_ALLOWED_LABEL = 'JPG, PNG, WebP o SVG';
const IMAGE_FILE_MAX_LABEL = '2 MB';
const IMAGE_FILE_REQUIREMENTS = `Formatos permitidos: ${IMAGE_FILE_ALLOWED_LABEL}. Peso máximo: ${IMAGE_FILE_MAX_LABEL}.`;
const IMAGE_FILE_RESTRICTION_TITLE = 'No se pudo cargar la imagen';
const IMAGE_FILE_ALLOWED_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp', 'image/svg+xml']);
const IMAGE_UPLOAD_MAX_EDGE = 1800;
const IMAGE_UPLOAD_QUALITY = 0.86;
const IMAGE_UPLOAD_NEW_TEMPLATE_BLOCK = '__new_template_image__';
const PAGE_PLACEHOLDER = '{pagina}';
const PAGE_PLACEHOLDER_REGEX = /\{pagina\}/gi;
const NOTE_MAX_LENGTH = 100;
const ALLOWED_FONT_SIZES = [20, 30, 40, 50, 60, 70, 80, 90, 100];
const DEFAULT_FONT_SIZE = 40;
const STRAIGHT_LINE_COLOR_OPTIONS = [
    { value: '#111827', label: 'Negro' },
    { value: '#ef4444', label: 'Rojo' },
    { value: '#2563eb', label: 'Azul' },
    { value: '#10b981', label: 'Verde' },
];
const STRAIGHT_LINE_WIDTH_OPTIONS = [1, 2, 3, 5, 8, 12];
const BOX_BORDER_OPTIONS = [
    { value: 'solid', label: 'Sólida' },
    { value: 'dashed', label: 'Segmentada' },
    { value: 'dotted', label: 'Punteada' },
    { value: 'dashdot', label: 'Guion punto' },
];
const FONT_SIZE_MAP = {
    '1': '20px',
    '2': '30px',
    '3': '40px',
    '4': '50px',
    '5': '60px',
    '6': '80px',
    '7': '100px',
};
const FONT_KEYWORD_MAP = {
    'xx-small': '20px',
    'x-small': '20px',
    'small': '30px',
    'medium': '40px',
    'large': '50px',
    'x-large': '80px',
    'xx-large': '100px',
};

const loadImageFromFile = (file) => new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
        URL.revokeObjectURL(objectUrl);
        resolve(img);
    };
    img.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        reject(new Error('No se pudo cargar la imagen.'));
    };
    img.src = objectUrl;
});

const parseSvgDimension = (value) => {
    const raw = String(value || '').trim();
    const match = /^(\d+(?:\.\d+)?)/.exec(raw);
    const numeric = match ? Number(match[1]) : NaN;
    return Number.isFinite(numeric) && numeric > 0 ? numeric : null;
};

const readSvgFileDimensions = (file) => new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => {
        try {
            const parser = new DOMParser();
            const doc = parser.parseFromString(String(reader.result || ''), 'image/svg+xml');
            const svg = doc.querySelector('svg');
            if (!svg) {
                resolve(null);
                return;
            }
            const width = parseSvgDimension(svg.getAttribute('width'));
            const height = parseSvgDimension(svg.getAttribute('height'));
            if (width && height) {
                resolve({ width, height });
                return;
            }
            const viewBox = svg.getAttribute('viewBox');
            const parts = String(viewBox || '').trim().split(/[\s,]+/).map(Number);
            if (parts.length === 4 && parts.every(Number.isFinite) && parts[2] > 0 && parts[3] > 0) {
                resolve({ width: parts[2], height: parts[3] });
                return;
            }
            resolve(null);
        } catch {
            resolve(null);
        }
    };
    reader.onerror = () => resolve(null);
    reader.readAsText(file);
});

const isSvgImageFile = (file) => (
    file?.type === 'image/svg+xml' || /\.svg$/i.test(file?.name || '')
);

const isAllowedTemplateImageFile = (file) => (
    IMAGE_FILE_ALLOWED_TYPES.has(file?.type) || isSvgImageFile(file)
);

const prepareTemplateImageUpload = async (file) => {
    if (isSvgImageFile(file)) {
        const dimensions = await readSvgFileDimensions(file);
        if (dimensions) {
            return { file, width: dimensions.width, height: dimensions.height };
        }
        const image = await loadImageFromFile(file);
        return {
            file,
            width: image.naturalWidth || image.width || 1,
            height: image.naturalHeight || image.height || 1,
        };
    }
    return compressTemplateImageFile(file);
};

const canvasToBlob = (canvas, type, quality) => new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), type, quality);
});

const compressTemplateImageFile = async (file) => {
    const image = await loadImageFromFile(file);
    const sourceWidth = image.naturalWidth || image.width;
    const sourceHeight = image.naturalHeight || image.height;
    const scale = Math.min(1, IMAGE_UPLOAD_MAX_EDGE / Math.max(sourceWidth, sourceHeight));
    const width = Math.max(1, Math.round(sourceWidth * scale));
    const height = Math.max(1, Math.round(sourceHeight * scale));
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
        throw new Error('No se pudo preparar la imagen.');
    }
    ctx.drawImage(image, 0, 0, width, height);

    const preferredType = 'image/webp';
    let blob = await canvasToBlob(canvas, preferredType, IMAGE_UPLOAD_QUALITY);
    let extension = 'webp';
    if (!blob) {
        blob = await canvasToBlob(canvas, 'image/jpeg', IMAGE_UPLOAD_QUALITY);
        extension = 'jpg';
    }
    if (!blob) {
        throw new Error('No se pudo comprimir la imagen.');
    }

    const baseName = (file.name || 'imagen')
        .replace(/\.[^.]+$/, '')
        .replace(/[^a-z0-9_-]+/gi, '-')
        .replace(/^-+|-+$/g, '')
        || 'imagen';

    return {
        file: new File([blob], `${baseName}.${extension}`, { type: blob.type || preferredType }),
        width,
        height,
    };
};

const isDesignNode = (node) => {
    const symbolLabel = node?.data?.symbolLabel;
    return symbolLabel === 'Box'
        || symbolLabel === 'StraightLine'
        || symbolLabel === 'SymbolDrawer'
        || node?.type === 'boxNode'
        || node?.type === 'straightLineNode'
        || node?.type === 'symbolDrawerNode';
};

const clampNumber = (value, min, max, fallback) => {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return fallback;
    return Math.min(max, Math.max(min, numeric));
};

const normalizeHex = (value, fallback = '#111827') => {
    const raw = typeof value === 'string' ? value.trim() : '';
    return /^#([0-9a-fA-F]{6})$/.test(raw) ? raw : fallback;
};

const containsPagePlaceholder = (value) => {
    if (typeof value !== 'string') return false;
    return value.toLowerCase().includes(PAGE_PLACEHOLDER);
};

const ensurePagePlaceholder = (value) => {
    if (typeof value !== 'string') return PAGE_PLACEHOLDER;
    if (containsPagePlaceholder(value)) return value;
    if (value.trim().length === 0) return PAGE_PLACEHOLDER;
    const needsSpacer = /\s$/.test(value) || value.endsWith('\n');
    return `${value}${needsSpacer ? '' : ' '}${PAGE_PLACEHOLDER}`;
};

const removePagePlaceholder = (value) => {
    if (typeof value !== 'string') return '';
    return value
        .replace(PAGE_PLACEHOLDER_REGEX, '')
        .replace(/\s{2,}/g, ' ')
        .replace(/\s+\n/g, '\n')
        .replace(/\n\s+/g, '\n')
        .trim();
};

const StraightLineIcon = () => (
    <span className="straight-line-tool-icon" aria-hidden="true" />
);

const SymbolDrawerIcon = () => (
    <span className="symbol-drawer-tool-icon" aria-hidden="true">
        <span />
        <span />
        <span />
        <span />
    </span>
);

const getPlainText = (html) => {
    if (typeof window === 'undefined') return '';
    const temp = window.document.createElement('div');
    temp.innerHTML = html || '';
    return temp.textContent || temp.innerText || '';
};

const placeCursorAtEnd = (element) => {
    if (!element || typeof window === 'undefined') return;
    const range = window.document.createRange();
    range.selectNodeContents(element);
    range.collapse(false);
    const selection = window.getSelection();
    if (!selection) return;
    selection.removeAllRanges();
    selection.addRange(range);
};

const clampToAllowedFontSize = (sizePx) => {
    const size = Number(sizePx);
    if (Number.isNaN(size)) return DEFAULT_FONT_SIZE;
    return ALLOWED_FONT_SIZES.reduce((closest, current) => (
        Math.abs(current - size) < Math.abs(closest - size) ? current : closest
    ), ALLOWED_FONT_SIZES[0]);
};

const normalizeFontSizeStyles = (value) => {
    if (typeof value !== 'string') return '';
    let output = value;
    output = output.replace(/style="([^"]*)"/gi, (match, styles) => {
        const declarations = styles
            .split(';')
            .map((item) => item.trim())
            .filter(Boolean);
        let fontSizeValue = null;
        declarations.forEach((decl) => {
            const [prop, val] = decl.split(':').map((part) => part.trim().toLowerCase());
            if (prop === 'font-size' && val) {
                const normalized = FONT_KEYWORD_MAP[val] || val;
                if (/^\d+(?:\.\d+)?px$/.test(normalized)) {
                    const clamped = clampToAllowedFontSize(parseFloat(normalized));
                    fontSizeValue = `${clamped}px`;
                }
            }
        });
        if (fontSizeValue) {
            return `style="font-size:${fontSizeValue}"`;
        }
        return '';
    });
    output = output.replace(/font-size:\s*([\w-]+)(?=;|"|')/gi, (match, size) => {
        const lower = `${size}`.toLowerCase();
        const normalized = FONT_KEYWORD_MAP[lower] || `${size}`;
        if (/^\d+(?:\.\d+)?px$/.test(normalized)) {
            const clamped = clampToAllowedFontSize(parseFloat(normalized));
            return `font-size:${clamped}px`;
        }
        return match;
    });
    output = output.replace(/<font\s+[^>]*size="?(\d)"?[^>]*>/gi, (match, size) => {
        const fontSize = FONT_SIZE_MAP[size] || FONT_SIZE_MAP['3'];
        return `<span style="font-size:${fontSize}">`;
    });
    output = output.replace(/<\/font>/gi, '</span>');
    return output;
};

const ensureDefaultFontSize = (html) => {
    if (typeof html !== 'string') return '';
    if (html.trim().length === 0) return '';
    if (/font-size:\s*\d+(?:\.\d+)?px/i.test(html)) {
        return html;
    }
    return `<span style="font-size:${DEFAULT_FONT_SIZE}px">${html}</span>`;
};

const NOTE_BOLD_MARKER = '\u200b';

const stripNoteEditMarkers = (html) => (
    typeof html === 'string'
        ? html
            .replace(new RegExp(NOTE_BOLD_MARKER, 'g'), '')
            .replace(/<(strong|b)>\s*<\/\1>/gi, '')
        : ''
);

const closestBoldElement = (node, root) => {
    if (!node || !root) return null;
    let current = node.nodeType === 1 ? node : node.parentNode;
    while (current && current !== root) {
        const tag = current.tagName ? current.tagName.toLowerCase() : '';
        if (tag === 'strong' || tag === 'b') return current;
        current = current.parentNode;
    }
    return null;
};

const closestTemplateTextEditor = (node) => {
    if (!node || typeof document === 'undefined') return null;
    const element = node.nodeType === 1 ? node : node.parentNode;
    return element?.closest?.('.template-notes-editor, [data-template-text-editor]') || null;
};

const normalizeImageRatioOption = (value) => (
    IMAGE_RATIO_OPTIONS.some((option) => option.value === value) ? value : TEMPLATE_IMAGE_RATIO_DEFAULT
);

const RatioIcon = ({ variant }) => (
    <svg
        className="template-media-ratio-icon"
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
    >
        <rect
            x={variant === 'rectangle' ? 3 : 5}
            y={variant === 'rectangle' ? 7 : 5}
            width={variant === 'rectangle' ? 18 : 14}
            height={variant === 'rectangle' ? 10 : 14}
            rx="3"
            stroke="currentColor"
            strokeWidth="2"
            fill="none"
        />
    </svg>
);

const AddImageIcon = () => (
    <svg
        className="template-media-add-icon"
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
    >
        <rect x="4" y="5" width="16" height="14" rx="3" stroke="currentColor" strokeWidth="1.8" />
        <path d="M7 15l3-3 3 3 4-4 3 3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        <circle cx="10" cy="9" r="1.4" stroke="currentColor" strokeWidth="1.4" />
        <path d="M12 3v6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        <path d="M9 6h6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
);

const TrashIcon = () => (
    <svg
        className="template-media-trash-icon"
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
    >
        <path
            d="M6 7h12l-1 12H7L6 7z"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
        <path d="M10 11v6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        <path d="M14 11v6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        <path d="M4 7h16" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        <path d="M9 7V4h6v3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const DuplicateIcon = () => (
    <svg
        className="template-media-duplicate-icon"
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
    >
        <rect x="8" y="8" width="10" height="10" rx="2" stroke="currentColor" strokeWidth="1.8" />
        <path d="M6 14H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v1" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
);

export const MenuBar = () => {
    const dispatch = useDispatch();
    const angleOffset = useSelector((state) => state.editor.angleOffset);
    const diagramHistory = useSelector(
        (state) => state.diagram.diagramHistory
      );
    const pageSize = useSelector((state) => state.view.pageSize);
    const templateSettings = useSelector((state) => state.view.templateSettings) || {};
    const templatePageOption = templateSettings.pageOption || 'none';
    const templateFooterOption = templateSettings.footerOption || 'none';
    const templateFooterText = templateSettings.footerText || '';
    const templateBlocks = Array.isArray(templateSettings.blocks) ? templateSettings.blocks : [];
    const selectedTemplateBlockId = templateSettings.selectedBlockId || null;
    const selectedTemplateBlock = templateBlocks.find((block) => block.id === selectedTemplateBlockId) || null;
    const selectedImageRatio = normalizeImageRatioOption(selectedTemplateBlock?.imageRatio);
    const straightLineTool = useSelector((state) => state.view.straightLineTool) || {};
    const symbolDrawerTool = useSelector((state) => state.view.symbolDrawerTool) || {};
    const straightLineColor = straightLineTool.color || '#111827';
    const straightLineStrokeWidth = Number(straightLineTool.strokeWidth) || 3;
    const allNodes = useSelector((state) => state.node.nodes);
    const selectedNodeIdsGlobal = useSelector((state) => state.node.selectedNodeIds) || [];
    const selectedEdgeIdsGlobal = useSelector((state) => state.edge.selectedEdgeIds) || [];
    const diagramLoading = useSelector((state) => state.diagram.diagramLoading);
    const selectedMenuNodes = useMemo(() => {
        if (!Array.isArray(allNodes) || selectedNodeIdsGlobal.length === 0) return [];
        const ids = new Set(selectedNodeIdsGlobal);
        return allNodes.filter((node) => ids.has(node.id));
    }, [allNodes, selectedNodeIdsGlobal]);
    const isOnlyDesignNodeSelection = selectedMenuNodes.length > 0
        && selectedMenuNodes.every(isDesignNode)
        && selectedEdgeIdsGlobal.length === 0;
    const selectedDesignNode = selectedMenuNodes.length === 1 && selectedEdgeIdsGlobal.length === 0 && isDesignNode(selectedMenuNodes[0])
        ? selectedMenuNodes[0]
        : null;
    const selectedDesignType = selectedDesignNode?.data?.symbolLabel || null;
    const showNodeToolbar = !diagramLoading && (
        !isOnlyDesignNodeSelection && (selectedNodeIdsGlobal.length > 0 || selectedEdgeIdsGlobal.length > 0)
    );
    const handlesHidden = useSelector((state) => state.view.handlesHidden);
    const hasTemplateContent = useMemo(() => (
        templatePageOption !== 'none'
        || templateFooterOption !== 'none'
        || templateBlocks.length > 0
    ), [templatePageOption, templateFooterOption, templateBlocks.length]);

    const [showTemplateBuilder, setShowTemplateBuilder] = useState(() => hasTemplateContent);
    const [isFooterModalOpen, setIsFooterModalOpen] = useState(false);
    const [footerModalInput, setFooterModalInput] = useState(templateFooterText);
    const footerModalPreviousOption = useRef(templateFooterOption).current;
    const [footerModalIncludePage, setFooterModalIncludePage] = useState(containsPagePlaceholder(templateFooterText));
    const imageUploadInputRef = useRef(null);
    const imageUploadTargetBlockIdRef = useRef(null);
    const [imageUploadError, setImageUploadError] = useState('');
    const [imageRestrictionModal, setImageRestrictionModal] = useState(null);
    const [isImageUploading, setImageUploading] = useState(false);
    const [imageUploadCursor, setImageUploadCursor] = useState(null);
    const [noteValue, setNoteValue] = useState('');
    const [noteBoldActive, setNoteBoldActive] = useState(false);
    const noteEditorRef = useRef(null);
    const autoOpenTemplateRef = useRef(false);
    const sanitizeNoteHtml = useCallback((value) => {
        const base = typeof value === 'string' ? normalizeFontSizeStyles(stripNoteEditMarkers(value)) : '';
        const sanitized = sanitizeHtml(base, {
            allowedTags: ['b', 'strong', 'i', 'em', 'span', 'br', 'p'],
            allowedAttributes: {
                span: ['style'],
                p: ['style'],
            },
            transformTags: {
                font: (tagName, attribs) => {
                    const size = attribs?.size;
                    const fontSize = FONT_SIZE_MAP[size] || FONT_SIZE_MAP['3'];
                    if (fontSize) {
                        return {
                            tagName: 'span',
                            attribs: {
                                style: `font-size:${fontSize}`,
                            },
                        };
                    }
                    return { tagName: 'span' };
                },
                div: 'p',
            },
            textFilter: (text) => text.replace(/\u00a0/g, ' '),
            parser: {
                lowerCaseAttributeNames: false,
            },
            parseStyleAttributes: false,
        }).trim();
        return ensureDefaultFontSize(sanitized);
    }, []);

    const noteCharacterCount = useMemo(() => getPlainText(noteValue).length, [noteValue]);

    const updateNoteBoldState = useCallback(() => {
        if (typeof window === 'undefined' || typeof document === 'undefined') {
            setNoteBoldActive(false);
            return;
        }
        const selection = window.getSelection();
        const anchorNode = selection?.anchorNode || null;
        const focusNode = selection?.focusNode || null;
        const editorElement = closestTemplateTextEditor(anchorNode) || noteEditorRef.current;
        if (!editorElement) {
            setNoteBoldActive(false);
            return;
        }
        const elementNodeType = window.Node?.ELEMENT_NODE || 1;
        const selectionBelongsToEditor =
            !!anchorNode
            && editorElement.contains(anchorNode.nodeType === elementNodeType ? anchorNode : anchorNode.parentNode)
            && (!focusNode || editorElement.contains(focusNode.nodeType === elementNodeType ? focusNode : focusNode.parentNode));
        if (document.activeElement !== editorElement && !selectionBelongsToEditor) {
            setNoteBoldActive(false);
            return;
        }
        setNoteBoldActive(!!closestBoldElement(anchorNode, editorElement));
    }, []);

    const handlePageSize = (size) => {
        dispatch(setPageSize(size));
    };

    const ensureTemplateDesignMode = () => {
        if (templatePageOption === 'none') {
            dispatch(setTemplateAnchor('right'));
            dispatch(setTemplatePageOption('first-page'));
        }
        setShowTemplateBuilder(true);
    };

    useEffect(() => {
        if (typeof window === 'undefined') return undefined;
        const handleTemplateOverlayFocus = () => {
            setShowTemplateBuilder(true);
        };
        const handleTemplateImageUploadRequest = (event) => {
            const blockId = event?.detail?.blockId;
            if (typeof blockId !== 'string' || !blockId) return;
            imageUploadTargetBlockIdRef.current = blockId;
            setImageUploadError('');
            imageUploadInputRef.current?.click();
        };
        window.addEventListener('template-anchor-overlay-focus', handleTemplateOverlayFocus);
        window.addEventListener('template-image-upload-request', handleTemplateImageUploadRequest);
        return () => {
            window.removeEventListener('template-anchor-overlay-focus', handleTemplateOverlayFocus);
            window.removeEventListener('template-image-upload-request', handleTemplateImageUploadRequest);
        };
    }, []);

    useEffect(() => {
        if (hasTemplateContent) {
            if (!autoOpenTemplateRef.current) {
                setShowTemplateBuilder(true);
                autoOpenTemplateRef.current = true;
            }
        } else {
            autoOpenTemplateRef.current = false;
        }
    }, [hasTemplateContent]);

    useEffect(() => {
        if (selectedDesignNode) {
            setShowTemplateBuilder(true);
        }
    }, [selectedDesignNode]);

    useEffect(() => {
        if (!isFooterModalOpen) {
            setFooterModalInput(templateFooterText);
            setFooterModalIncludePage(containsPagePlaceholder(templateFooterText));
        }
    }, [templateFooterText, isFooterModalOpen]);

    useEffect(() => {
        if (!selectedTemplateBlock || selectedTemplateBlock.type !== 'text') {
            setNoteValue('');
            setNoteBoldActive(false);
            if (noteEditorRef.current && noteEditorRef.current.innerHTML !== '') {
                noteEditorRef.current.innerHTML = '';
            }
            return;
        }
        const sanitized = sanitizeNoteHtml(selectedTemplateBlock.text || '');
        const editorElement = noteEditorRef.current;
        const isFocused = editorElement && typeof window !== 'undefined' && window.document.activeElement === editorElement;
        if (!isFocused) {
            if (noteValue !== sanitized) {
                setNoteValue(sanitized);
            }
            if (editorElement && editorElement.innerHTML !== sanitized) {
                editorElement.innerHTML = sanitized;
            }
        }
        updateNoteBoldState();
    }, [noteValue, sanitizeNoteHtml, selectedTemplateBlock, updateNoteBoldState]);

    useEffect(() => {
        if (!selectedTemplateBlock || selectedTemplateBlock.type !== 'text') {
            setNoteBoldActive(false);
            return undefined;
        }
        if (typeof document === 'undefined') return undefined;
        document.addEventListener('selectionchange', updateNoteBoldState);
        return () => {
            document.removeEventListener('selectionchange', updateNoteBoldState);
        };
    }, [selectedTemplateBlock, updateNoteBoldState]);

    useEffect(() => {
        setImageUploadError('');
    }, [selectedTemplateBlockId]);

    useEffect(() => {
        if (templatePageOption === 'none') {
            dispatch(selectTemplateBlock(null));
        }
    }, [templatePageOption, dispatch]);

    const handleFooterModalCancel = () => {
        setIsFooterModalOpen(false);
        setFooterModalInput(templateFooterText);
        setFooterModalIncludePage(containsPagePlaceholder(templateFooterText));
        if (footerModalPreviousOption !== templateFooterOption) {
            dispatch(setTemplateFooterOption(footerModalPreviousOption));
        }
    };

    const handleFooterModalSubmit = (event) => {
        event.preventDefault();
        let candidate = footerModalInput;
        candidate = footerModalIncludePage
            ? ensurePagePlaceholder(candidate)
            : removePagePlaceholder(candidate);
        const trimmed = candidate.trim();
        dispatch(setTemplateFooterText(trimmed));
        setFooterModalInput(trimmed);
        setFooterModalIncludePage(containsPagePlaceholder(trimmed));
        setIsFooterModalOpen(false);
    };

    const handleFooterModalInputChange = (event) => {
        const { value } = event.target;
        setFooterModalInput(value);
        setFooterModalIncludePage(containsPagePlaceholder(value));
    };

    const handleFooterModalIncludePageChange = (event) => {
        const { checked } = event.target;
        const currentValue = footerModalInput;
        const nextValue = checked
            ? ensurePagePlaceholder(currentValue)
            : removePagePlaceholder(currentValue);
        setFooterModalInput(nextValue);
        setFooterModalIncludePage(containsPagePlaceholder(nextValue));
    };

    const isFooterModalSaveDisabled = footerModalInput.trim().length === 0;
    const isTemplateActive = templatePageOption !== 'none';

    useEffect(() => {
        if (!isImageUploading) {
            document.body.classList.remove('template-image-uploading-cursor');
            return undefined;
        }
        document.body.classList.add('template-image-uploading-cursor');
        const handleMouseMove = (event) => {
            setImageUploadCursor({ x: event.clientX, y: event.clientY });
        };
        window.addEventListener('mousemove', handleMouseMove);
        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            document.body.classList.remove('template-image-uploading-cursor');
        };
    }, [isImageUploading]);

    const toggleHandlesVisibility = useCallback(() => {
        dispatch(setHandlesHidden(!handlesHidden));
    }, [dispatch, handlesHidden]);

    const handleActivateImageDesign = (event) => {
        ensureTemplateDesignMode();
        setImageUploadError('');
        if (event?.clientX && event?.clientY) {
            setImageUploadCursor({ x: event.clientX, y: event.clientY });
        }
        imageUploadTargetBlockIdRef.current = IMAGE_UPLOAD_NEW_TEMPLATE_BLOCK;
        imageUploadInputRef.current?.click();
    };

    const handleActivateBoxDesign = () => {
        ensureTemplateDesignMode();
        dispatch(activateBoxTool());
    };

    const handleActivateStraightLineDesign = () => {
        ensureTemplateDesignMode();
        dispatch(activateStraightLineTool());
    };

    const handleStraightLineColorChange = (event) => {
        dispatch(setStraightLineToolColor(event.target.value));
    };

    const handleStraightLineStrokeWidthChange = (event) => {
        dispatch(setStraightLineToolStrokeWidth(event.target.value));
    };

    const updateSelectedBoxAttribute = (name, value, extra = {}) => {
        if (!selectedDesignNode || selectedDesignType !== 'Box') return;
        const existing = selectedDesignNode.data?.attributes?.[name] || {};
        dispatch(setNodeAttributes({
            nodeId: selectedDesignNode.id,
            attributes: {
                [name]: {
                    ...existing,
                    ...extra,
                    value,
                    updatedAt: Date.now(),
                },
            },
        }));
    };

    const updateSelectedDesignNode = (updater) => {
        if (!selectedDesignNode || typeof updater !== 'function') return;
        const nextNodes = (Array.isArray(allNodes) ? allNodes : []).map((node) => (
            node.id === selectedDesignNode.id ? updater(node) : node
        ));
        dispatch(setNodesActionCreator({ nodes: nextNodes }));
    };

    const handleSelectedLineColorChange = (event) => {
        const color = normalizeHex(event.target.value);
        updateSelectedDesignNode((node) => ({
            ...node,
            data: {
                ...(node.data || {}),
                line: {
                    ...(node.data?.line || {}),
                    color,
                },
            },
        }));
    };

    const handleSelectedLineStrokeChange = (event) => {
        const strokeWidth = Math.round(clampNumber(event.target.value, 1, 24, 3));
        updateSelectedDesignNode((node) => ({
            ...node,
            data: {
                ...(node.data || {}),
                line: {
                    ...(node.data?.line || {}),
                    strokeWidth,
                },
            },
        }));
    };

    const handleSelectedDrawerWidthChange = (event) => {
        const width = Math.round(clampNumber(event.target.value, 180, 1600, 320));
        updateSelectedDesignNode((node) => ({
            ...node,
            width,
            data: {
                ...(node.data || {}),
                drawer: {
                    ...(node.data?.drawer || {}),
                    width,
                },
            },
            style: {
                ...(node.style || {}),
                width: `${width}px`,
            },
        }));
    };

    const handleAddSymbolDrawer = () => {
        ensureTemplateDesignMode();
        dispatch(activateSymbolDrawerTool());
    };

    const handleActivateTextDesign = () => {
        ensureTemplateDesignMode();
        dispatch(activateTemplateTextTool());
    };

    const handleRemoveBlock = (blockId) => {
        dispatch(removeTemplateBlock(blockId));
    };

    const handleDuplicateBlock = (blockId) => {
        dispatch(duplicateTemplateBlock(blockId));
    };

    const handleSelectBlock = (blockId) => {
        dispatch(selectTemplateBlock(blockId));
    };

    const dragIndexRef = useRef(null);
    const handleBlockDragStart = (event, index) => {
        dragIndexRef.current = index;
        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.setData('text/plain', String(index));
      };

    const handleBlockDragOver = (event) => {
        event.preventDefault();
    };

    const handleBlockDrop = (event, index) => {
        event.preventDefault();
        const fromIndex = dragIndexRef.current;
        dragIndexRef.current = null;
        if (typeof fromIndex !== 'number') {
            return;
        }
        const maxIndex = templateBlocks.length;
        const rawTarget = typeof index === 'number' ? index : maxIndex;
        const targetIndex = Math.max(0, Math.min(rawTarget, maxIndex));
        if (fromIndex === targetIndex) {
            return;
        }
        dispatch(reorderTemplateBlocks({ fromIndex, toIndex: targetIndex }));
    };

    const handleBlockDragEnd = () => {
        dragIndexRef.current = null;
    };

    const handleImageUploadTrigger = (event) => {
        if (!isTemplateActive || !selectedTemplateBlock || selectedTemplateBlock.type !== 'image') {
            return;
        }
        if (event?.clientX && event?.clientY) {
            setImageUploadCursor({ x: event.clientX, y: event.clientY });
        }
        imageUploadTargetBlockIdRef.current = selectedTemplateBlock.id;
        setImageUploadError('');
        imageUploadInputRef.current?.click();
    };

    const showImageRestrictionModal = useCallback((message) => {
        setImageUploadError('');
        setImageRestrictionModal({
            title: IMAGE_FILE_RESTRICTION_TITLE,
            message,
        });
    }, []);

    const closeImageRestrictionModal = useCallback(() => {
        setImageRestrictionModal(null);
    }, []);

    const handleImageFileInputChange = async (event) => {
        const file = event.target.files && event.target.files[0];
        event.target.value = '';
        if (!file) return;
        const requestedTargetBlockId = imageUploadTargetBlockIdRef.current;
        const targetBlockId = requestedTargetBlockId === IMAGE_UPLOAD_NEW_TEMPLATE_BLOCK
            ? null
            : (requestedTargetBlockId || (selectedTemplateBlock?.type === 'image' ? selectedTemplateBlock.id : null));
        if (!isAllowedTemplateImageFile(file)) {
            showImageRestrictionModal(`Formato no compatible. ${IMAGE_FILE_REQUIREMENTS}`);
            return;
        }
        if (file.size > IMAGE_FILE_MAX_BYTES) {
            showImageRestrictionModal(`La imagen pesa más de ${IMAGE_FILE_MAX_LABEL}. ${IMAGE_FILE_REQUIREMENTS}`);
            return;
        }

        try {
            setImageUploading(true);
            if (!imageUploadCursor) {
                setImageUploadCursor({ x: window.innerWidth / 2, y: window.innerHeight / 2 });
            }
            setImageUploadError('');
            const optimized = await prepareTemplateImageUpload(file);
            if (optimized.file.size > IMAGE_FILE_MAX_BYTES) {
                showImageRestrictionModal(isSvgImageFile(file)
                    ? `La imagen SVG pesa más de ${IMAGE_FILE_MAX_LABEL}. ${IMAGE_FILE_REQUIREMENTS}`
                    : `No se pudo dejar la imagen debajo de ${IMAGE_FILE_MAX_LABEL} después de comprimirla. ${IMAGE_FILE_REQUIREMENTS}`);
                return;
            }
            const media = await uploadEditorTemplateMedia(optimized.file);
            const nextImage = {
                src: media?.src,
                url: media?.url || media?.src,
                mediaId: media?.id || media?.filename || null,
                name: file.name,
                width: Number(media?.width) || optimized.width,
                height: Number(media?.height) || optimized.height,
                mime: media?.mime || optimized.file.type,
                size: Number(media?.size) || optimized.file.size,
            };
            if (!nextImage.src) {
                throw new Error('No se recibió la URL de la imagen.');
            }
            if (targetBlockId) {
                dispatch(updateTemplateBlock({
                    id: targetBlockId,
                    changes: { image: nextImage },
                }));
            } else {
                setShowTemplateBuilder(true);
                dispatch(activateTemplateImageTool({ image: nextImage }));
            }
            imageUploadTargetBlockIdRef.current = null;
        } catch (err) {
            setImageUploadError(err?.data?.message || err?.message || `No se pudo subir la imagen. ${IMAGE_FILE_REQUIREMENTS}`);
        } finally {
            setImageUploading(false);
        }
    };

    const handleImageRatioChange = (ratioValue) => {
        if (!selectedTemplateBlock || selectedTemplateBlock.type !== 'image') return;
        dispatch(updateTemplateBlock({ id: selectedTemplateBlock.id, changes: { imageRatio: ratioValue } }));
    };

    const handleImageRatioToggle = () => {
        if (!selectedTemplateBlock || selectedTemplateBlock.type !== 'image') return;
        const nextRatio = selectedImageRatio === 'square' ? 'rectangle' : 'square';
        handleImageRatioChange(nextRatio);
    };

    const handleNoteInput = useCallback((event) => {
        if (!selectedTemplateBlock || selectedTemplateBlock.type !== 'text') {
            return;
        }
        const html = normalizeFontSizeStyles(event.currentTarget.innerHTML);
        const plainTextLength = getPlainText(html).length;
        if (plainTextLength > NOTE_MAX_LENGTH) {
            if (noteEditorRef.current) {
                noteEditorRef.current.innerHTML = noteValue || '';
                placeCursorAtEnd(noteEditorRef.current);
            }
            return;
        }
        const sanitized = sanitizeNoteHtml(html);
        setNoteValue(sanitized);
        if (selectedTemplateBlock?.id) {
            dispatch(updateTemplateBlock({ id: selectedTemplateBlock.id, changes: { text: sanitized } }));
        }
        requestAnimationFrame(updateNoteBoldState);
    }, [dispatch, noteValue, sanitizeNoteHtml, selectedTemplateBlock, updateNoteBoldState]);

    const handleNoteBeforeInput = useCallback((event) => {
        if (event.defaultPrevented) return;
        if (!noteBoldActive || !selectedTemplateBlock || selectedTemplateBlock.type !== 'text') return;
        if (event.inputType !== 'insertText' || typeof event.data !== 'string' || event.data.length === 0) return;
        const selection = window.getSelection();
        const editorElement = closestTemplateTextEditor(selection?.anchorNode)
            || closestTemplateTextEditor(event.target)
            || noteEditorRef.current;
        const targetBlockId = editorElement?.dataset?.templateTextEditor || selectedTemplateBlock.id;
        if (!editorElement || !targetBlockId || !selection || selection.rangeCount === 0) return;
        const range = selection.getRangeAt(0);
        if (!editorElement.contains(range.commonAncestorContainer)) return;

        event.preventDefault();
        range.deleteContents();

        let boldElement = closestBoldElement(range.startContainer, editorElement);
        if (!boldElement) {
            boldElement = document.createElement('strong');
            range.insertNode(boldElement);
            range.selectNodeContents(boldElement);
            range.collapse(false);
        }

        const textNode = document.createTextNode(event.data);
        range.insertNode(textNode);
        Array.from(boldElement.childNodes).forEach((child) => {
            if (child !== textNode && child.nodeType === 3 && child.nodeValue === NOTE_BOLD_MARKER) {
                child.remove();
            }
        });
        range.setStartAfter(textNode);
        range.collapse(true);
        selection.removeAllRanges();
        selection.addRange(range);

        if (editorElement === noteEditorRef.current) {
            const sanitized = sanitizeNoteHtml(editorElement.innerHTML);
            setNoteValue(sanitized);
            dispatch(updateTemplateBlock({ id: targetBlockId, changes: { text: sanitized } }));
        }
        setNoteBoldActive(true);
    }, [dispatch, noteBoldActive, sanitizeNoteHtml, selectedTemplateBlock]);

    useEffect(() => {
        if (!noteBoldActive || !isTemplateActive) return undefined;
        const handleDocumentBeforeInput = (event) => {
            if (!closestTemplateTextEditor(event.target)) return;
            handleNoteBeforeInput(event);
        };
        document.addEventListener('beforeinput', handleDocumentBeforeInput, true);
        return () => {
            document.removeEventListener('beforeinput', handleDocumentBeforeInput, true);
        };
    }, [handleNoteBeforeInput, isTemplateActive, noteBoldActive]);

    const handleNoteBlur = useCallback(() => {
        if (!selectedTemplateBlock || selectedTemplateBlock.type !== 'text') {
            return;
        }
        const sanitized = sanitizeNoteHtml(noteValue);
        setNoteValue(sanitized);
        if (noteEditorRef.current && noteEditorRef.current.innerHTML !== sanitized) {
            noteEditorRef.current.innerHTML = sanitized;
        }
        if (selectedTemplateBlock?.id) {
            dispatch(updateTemplateBlock({ id: selectedTemplateBlock.id, changes: { text: sanitized } }));
        }
        requestAnimationFrame(updateNoteBoldState);
    }, [dispatch, noteValue, sanitizeNoteHtml, selectedTemplateBlock, updateNoteBoldState]);

    const handleNoteToolbarAction = useCallback((action) => {
        if (!noteEditorRef.current || !isTemplateActive) return;
        if (!selectedTemplateBlock || selectedTemplateBlock.type !== 'text') return;
        if (action !== 'increase' && action !== 'decrease' && action !== 'bold') return;

        if (action === 'bold') {
            const selection = window.getSelection();
            const candidateEditor = closestTemplateTextEditor(selection?.anchorNode) || noteEditorRef.current;
            const editorElement = candidateEditor;
            const targetBlockId = editorElement?.dataset?.templateTextEditor || selectedTemplateBlock.id;
            if (!editorElement || !targetBlockId) return;
            editorElement.focus();
            if (!selection || selection.rangeCount === 0) return;
            const range = selection.getRangeAt(0);
            const selectionInsideEditor = editorElement.contains(range.commonAncestorContainer);
            if (!selectionInsideEditor) return;

            const persistTargetEditorHtml = () => {
                const sanitized = sanitizeNoteHtml(editorElement.innerHTML);
                if (editorElement === noteEditorRef.current) {
                    setNoteValue(sanitized);
                }
                dispatch(updateTemplateBlock({ id: targetBlockId, changes: { text: sanitized } }));
                return sanitized;
            };

            const activeBoldElement = closestBoldElement(range.startContainer, editorElement);
            if (!range.collapsed) {
                document.execCommand('bold', false, null);
                persistTargetEditorHtml();
                requestAnimationFrame(updateNoteBoldState);
                return;
            }

            if (activeBoldElement) {
                const spacer = document.createTextNode(NOTE_BOLD_MARKER);
                activeBoldElement.parentNode.insertBefore(spacer, activeBoldElement.nextSibling);
                range.setStart(spacer, spacer.length);
                range.collapse(true);
                selection.removeAllRanges();
                selection.addRange(range);
                setNoteBoldActive(false);
                return;
            }

            const strong = document.createElement('strong');
            const marker = document.createTextNode(NOTE_BOLD_MARKER);
            strong.appendChild(marker);
            range.insertNode(strong);
            range.setStart(marker, marker.length);
            range.collapse(true);
            selection.removeAllRanges();
            selection.addRange(range);
            setNoteBoldActive(true);
            requestAnimationFrame(updateNoteBoldState);
            return;
        }

        const currentRawHtml = noteEditorRef.current.innerHTML;
        const normalizedHtml = normalizeFontSizeStyles(currentRawHtml);
        const sanitized = sanitizeNoteHtml(normalizedHtml);

        if (getPlainText(sanitized).length === 0) {
            return;
        }

        const sizeMatch = sanitized.match(/font-size:\s*(\d+(?:\.\d+)?)px/i);
        const currentSize = sizeMatch ? clampToAllowedFontSize(sizeMatch[1]) : DEFAULT_FONT_SIZE;
        const sortedSizes = [...ALLOWED_FONT_SIZES];
        const currentIndex = sortedSizes.findIndex((size) => size >= currentSize);
        const effectiveIndex = currentIndex === -1 ? sortedSizes.indexOf(DEFAULT_FONT_SIZE) : currentIndex;
        let nextSize = currentSize;
        if (action === 'increase') {
            nextSize = sortedSizes[Math.min(effectiveIndex + 1, sortedSizes.length - 1)];
        } else if (action === 'decrease') {
            nextSize = sortedSizes[Math.max(effectiveIndex - 1, 0)];
        }

        const contentWithoutSpan = sanitized.replace(/<span[^>]*style="[^"]*font-size:[^";]+;?[^"]*"[^>]*>([\s\S]*?)<\/span>/gi, '$1');
        const effectiveContent = contentWithoutSpan.length > 0 ? contentWithoutSpan : sanitized;
        const updatedHtml = ensureDefaultFontSize(`<span style="font-size:${nextSize}px">${effectiveContent}</span>`);

        setNoteValue(updatedHtml);
        noteEditorRef.current.innerHTML = updatedHtml;
        placeCursorAtEnd(noteEditorRef.current);
        dispatch(updateTemplateBlock({ id: selectedTemplateBlock.id, changes: { text: updatedHtml } }));
        requestAnimationFrame(updateNoteBoldState);
    }, [dispatch, isTemplateActive, sanitizeNoteHtml, selectedTemplateBlock, updateNoteBoldState]);

    const onDragStart = (event, group, alias, variantAlias, overrideAngle, isRotable, symbolLabel) => {
        const effectiveAngle = typeof overrideAngle === 'number' ? overrideAngle : angleOffset;
        dispatch(clearSpawnPreview());
        prepareDragPayload(event, group, alias, effectiveAngle, isRotable, symbolLabel, variantAlias, DEFAULT_ICON_SCALE_CM);
    };

    const SubdiagramNode = () => {
        const symbolLabel = 'Subdiagram';
        const category = 'subdiagram';
        const subdiagram = Symbols[category][0];
      
        return (
          <div
            className='subdiagram_control--add'
              onDragStart={(event) =>
              onDragStart(event, category, subdiagram.alias, null, undefined, false, symbolLabel)
            }
            draggable
          >
            <div className='subdiagram_wrapper'>
              <div className='subdiagram_background'>
                <div className='subdiagram_border'>
                  <div className='subdiagram_icon--default'>
                    <img src='/assets/menubar/plus-azul.svg' />
                  </div>
                </div>
              </div>
              <div className='subdiagram_title'>Agregar</div>
            </div>
          </div>
        );
      };

      const renderBase = () => (
        <div
          className={`subdiagram_control--home${
            diagramHistory.length === 0 ? ' current' : ''
          }`}
        >
          <div className='subdiagram_wrapper'>
            <div className='subdiagram_background'>
              <div className='subdiagram_border'>
                <div className='subdiagram_icon--default'>
                  <img src='/assets/menubar/casa-gris.svg' />
                </div>
                <div className='subdiagram_icon--current'></div>
              </div>
            </div>
            <div className='subdiagram_title'>Base</div>
          </div>
        </div>
      );
    
      const renderHistory = () =>
        diagramHistory.map((entry, index) => {
          const isLast = index === diagramHistory.length - 1;
          return (
            <div
              key={index}
              className={`subdiagram_control--diagram${
                isLast ? ' current' : ''
              }`}
            >
              <div className='subdiagram_wrapper'>
                <div className='subdiagram_background'>
                  <div className='subdiagram_border'>
                    <div className='subdiagram_icon--default'></div>
                    <div className='subdiagram_icon--current'>
                      <img src='/assets/menubar/location-blue.svg' />
                    </div>
                  </div>
                </div>
                <div className='subdiagram_title'>{entry?.name || 'Sección B'}</div>
              </div>
            </div>
          );
        });

      const selectedBoxAttributes = selectedDesignType === 'Box'
        ? (selectedDesignNode?.data?.attributes || {})
        : {};
      const selectedBoxBackground = normalizeHex(selectedBoxAttributes.backgroundColor?.value, '#ffffff');
      const selectedBoxBorder = normalizeHex(selectedBoxAttributes.strokeColor?.value, '#111827');
      const selectedBoxStroke = Math.round(clampNumber(selectedBoxAttributes.strokeWidth?.value, 1, 24, 3));
      const selectedBoxBorderStyle = selectedBoxAttributes.borderStyle?.value || 'solid';
      const selectedBoxColumns = Math.round(clampNumber(selectedBoxAttributes.columns?.value, 1, 24, 1));
      const selectedBoxRows = Math.round(clampNumber(selectedBoxAttributes.rows?.value, 1, 24, 1));
      const selectedLine = selectedDesignType === 'StraightLine' ? (selectedDesignNode?.data?.line || {}) : {};
      const selectedLineColor = normalizeHex(selectedLine.color, '#111827');
      const selectedLineStroke = Math.round(clampNumber(selectedLine.strokeWidth, 1, 24, 3));
      const selectedDrawerWidth = Math.round(clampNumber(selectedDesignNode?.data?.drawer?.width, 180, 1600, 320));

      const SubdiagramNavigation = () => (
        <div className="container-tools subdiagram_controls">
          <div className="menu-tools-wrap subdiagram-tools-wrap">
            {renderBase()}
            {renderHistory()}
            <SubdiagramNode />
          </div>
        </div>
      );
    
    return (
    <div className={`menu-bar${showNodeToolbar ? ' menu-bar--selection' : ''}`}>
        <div className="menu-bar__overlay-root" id="menu-bar-overlay-root" />
        <div className="menu-bar__content">
        {isFooterModalOpen ? (
            <div className="template-footer-modal-backdrop">
                <div
                    className="template-footer-modal"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="template-footer-modal-title"
                >
                    <h4 id="template-footer-modal-title" className="template-footer-modal-title">
                        ¿Qué texto quieres que aparezca en el pie de página?
                    </h4>
                    <form className="template-footer-modal-form" onSubmit={handleFooterModalSubmit}>
                        <textarea
                            className="template-footer-modal-input"
                            value={footerModalInput}
                            onChange={handleFooterModalInputChange}
                            rows={3}
                            placeholder="Escribe el texto para el pie de página"
                            autoFocus
                            maxLength={200}
                            aria-label="Texto del pie de página"
                        />
                        <label className="template-footer-modal-toggle">
                            <input
                                type="checkbox"
                                className="template-footer-modal-toggle-input"
                                checked={footerModalIncludePage}
                                onChange={handleFooterModalIncludePageChange}
                            />
                            <span>Agregar número de hoja</span>
                            {footerModalIncludePage ? (
                                <span className="template-footer-modal-toggle-hint"></span>
                            ) : null}
                        </label>
                        <div className="template-footer-modal-actions">
                            <button
                                type="button"
                                className="template-footer-modal-button is-secondary"
                                onClick={handleFooterModalCancel}
                            >
                                Cancelar
                            </button>
                            <button
                                type="submit"
                                className={`template-footer-modal-button is-primary${isFooterModalSaveDisabled ? ' is-disabled' : ''}`}
                                disabled={isFooterModalSaveDisabled}
                            >
                                Guardar
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        ) : null}
        {imageRestrictionModal ? (
            <div className="template-file-restriction-modal-backdrop">
                <div
                    className="template-file-restriction-modal"
                    role="alertdialog"
                    aria-modal="true"
                    aria-labelledby="template-file-restriction-modal-title"
                    aria-describedby="template-file-restriction-modal-message"
                >
                    <h4 id="template-file-restriction-modal-title" className="template-file-restriction-modal-title">
                        {imageRestrictionModal.title}
                    </h4>
                    <p id="template-file-restriction-modal-message" className="template-file-restriction-modal-message">
                        {imageRestrictionModal.message}
                    </p>
                    <div className="template-file-restriction-modal-actions">
                        <button
                            type="button"
                            className="template-file-restriction-modal-button"
                            onClick={closeImageRestrictionModal}
                        >
                            Entendido
                        </button>
                    </div>
                </div>
            </div>
        ) : null}
        {isImageUploading && imageUploadCursor ? (
            <div
                className="template-image-upload-cursor"
                style={{
                    left: imageUploadCursor.x,
                    top: imageUploadCursor.y,
                }}
                aria-hidden="true"
            >
                <span className="template-image-upload-cursor__spinner" />
            </div>
        ) : null}
        <nav className="menu-nav">
            <SubdiagramNavigation />
            <div className='container-tools document-size'>
                <div className='menu-tools-wrap'>
                    <div className={`menu-tool ${pageSize === PAGE_SIZE_A4 ? 'selected' : ''}`}
                        onClick={() => handlePageSize(PAGE_SIZE_A4)}>
                        <img src={sizeCarta} alt="A4" />
                        <p>A4</p>
                    </div>
                    <div className={`menu-tool ${pageSize === PAGE_SIZE_A3 ? 'selected' : ''}`}
                        onClick={() => handlePageSize(PAGE_SIZE_A3)}>
                        <img src={sizeDobleCarta} alt="A3" />
                        <p>A3</p>
                    </div>
                    <div className={`menu-tool ${pageSize === PAGE_SIZE_ARCH_D ? 'selected' : ''}`}
                        onClick={() => handlePageSize(PAGE_SIZE_ARCH_D)}>
                        <img src={sizeArchd} alt="Arch D" />
                        <p>Arch D</p>
                    </div>
                </div>
                {/* <h3>Vistas</h3> */}
            </div>

            <div className='container-tools handle-visibility-controls'>
                <div className='menu-tools-wrap'>
                    <div
                        className={`menu-tool handle-visibility-toggle${handlesHidden ? ' selected' : ''}`}
                        onClick={toggleHandlesVisibility}
                    >
                        <img
                            src={handlesHidden ? iconHidden : iconVisible}
                            alt={handlesHidden ? 'Aparecer ayudas' : 'Ocultar ayudas'}
                        />
                        <p>{handlesHidden ? 'Ayudas' : 'Ayudas'}</p>
                    </div>
                </div>
            </div>

            <div className="container-tools design">
                <div className="menu-tools-wrap design-tools-wrap">
                    <button
                        type="button"
                        className="template-design-tool design-tool"
                        onClick={handleActivateBoxDesign}
                        aria-label="Agregar rectangulo"
                    >
                        <img className="template-design-tool__asset" src="/assets/ico-createsquare.svg" alt="" aria-hidden="true" />
                        <span className="template-design-tool__helper" role="tooltip">Agregar rectángulo</span>
                    </button>
                    <div className="straight-line-tool-wrap">
                        <button
                            type="button"
                            className={`template-design-tool design-tool${straightLineTool.active ? ' is-active' : ''}`}
                            onClick={handleActivateStraightLineDesign}
                            aria-label="Agregar linea recta"
                        >
                            <StraightLineIcon />
                            <span className="template-design-tool__helper" role="tooltip">Agregar línea recta</span>
                        </button>
                    </div>
                    <button
                        type="button"
                        className="template-design-tool design-tool"
                        onClick={handleActivateImageDesign}
                        aria-label="Agregar imagen"
                    >
                        <img className="template-design-tool__asset" src="/assets/ico-createimage.svg" alt="" aria-hidden="true" />
                        <span className="template-design-tool__helper" role="tooltip">Agregar imagen</span>
                    </button>
                    <button
                        type="button"
                        className="template-design-tool design-tool"
                        onClick={handleActivateTextDesign}
                        aria-label="Agregar texto"
                    >
                        <img className="template-design-tool__asset" src="/assets/ico-createtext.svg" alt="" aria-hidden="true" />
                        <span className="template-design-tool__helper" role="tooltip">Agregar texto</span>
                    </button>
                    <div className="symbol-drawer-wrap">
                        <button
                            type="button"
                            className={`template-design-tool design-tool${symbolDrawerTool.active ? ' is-active' : ''}`}
                            onClick={handleAddSymbolDrawer}
                            aria-label="Cajon de simbologia"
                        >
                            <SymbolDrawerIcon />
                            <span className="template-design-tool__helper" role="tooltip">Cajón de simbología</span>
                        </button>
                    </div>
                </div>
            </div>


                
            {showTemplateBuilder ? (
                <div className="container-tools template-controls is-active">
                    <div className="template-builder-panel">
                        <div className="template-builder-body">
                            {straightLineTool.active ? (
                                <div className="template-design-params" aria-label="Parámetros de línea recta">
                                    <label className="template-design-param">
                                        <span>Color de línea</span>
                                        <select value={straightLineColor} onChange={handleStraightLineColorChange}>
                                            {STRAIGHT_LINE_COLOR_OPTIONS.map((option) => (
                                                <option key={option.value} value={option.value}>{option.label}</option>
                                            ))}
                                        </select>
                                    </label>
                                    <label className="template-design-param">
                                        <span>Grosor</span>
                                        <select value={straightLineStrokeWidth} onChange={handleStraightLineStrokeWidthChange}>
                                            {STRAIGHT_LINE_WIDTH_OPTIONS.map((value) => (
                                                <option key={value} value={value}>{value}px</option>
                                            ))}
                                        </select>
                                    </label>
                                </div>
                            ) : null}
                            {selectedDesignNode ? (
                                <div className="template-design-node-panel" aria-label="Propiedades de elemento de diseño">
                                    {selectedDesignType === 'Box' ? (
                                        <>
                                            <label className="template-design-param">
                                                <span>Fondo</span>
                                                <input
                                                    type="color"
                                                    value={selectedBoxBackground}
                                                    onChange={(event) => updateSelectedBoxAttribute('backgroundColor', event.target.value, { label: 'Color de fondo', systemDefined: true })}
                                                />
                                            </label>
                                            <label className="template-design-param">
                                                <span>Borde</span>
                                                <input
                                                    type="color"
                                                    value={selectedBoxBorder}
                                                    onChange={(event) => updateSelectedBoxAttribute('strokeColor', event.target.value, { label: 'Color de borde', systemDefined: true })}
                                                />
                                            </label>
                                            <label className="template-design-param">
                                                <span>Linea</span>
                                                <select
                                                    value={selectedBoxBorderStyle}
                                                    onChange={(event) => updateSelectedBoxAttribute('borderStyle', event.target.value, { label: 'Estilo de borde' })}
                                                >
                                                    {BOX_BORDER_OPTIONS.map((option) => (
                                                        <option key={option.value} value={option.value}>{option.label}</option>
                                                    ))}
                                                </select>
                                            </label>
                                            <label className="template-design-param">
                                                <span>Grosor</span>
                                                <input
                                                    type="number"
                                                    min="1"
                                                    max="24"
                                                    value={selectedBoxStroke}
                                                    onChange={(event) => updateSelectedBoxAttribute('strokeWidth', Math.round(clampNumber(event.target.value, 1, 24, selectedBoxStroke)), { label: 'Ancho de borde (px)' })}
                                                />
                                            </label>
                                            <label className="template-design-param">
                                                <span>Columnas</span>
                                                <input
                                                    type="number"
                                                    min="1"
                                                    max="24"
                                                    value={selectedBoxColumns}
                                                    onChange={(event) => updateSelectedBoxAttribute('columns', Math.round(clampNumber(event.target.value, 1, 24, selectedBoxColumns)), { label: 'Columnas', systemDefined: true })}
                                                />
                                            </label>
                                            <label className="template-design-param">
                                                <span>Filas</span>
                                                <input
                                                    type="number"
                                                    min="1"
                                                    max="24"
                                                    value={selectedBoxRows}
                                                    onChange={(event) => updateSelectedBoxAttribute('rows', Math.round(clampNumber(event.target.value, 1, 24, selectedBoxRows)), { label: 'Filas', systemDefined: true })}
                                                />
                                            </label>
                                        </>
                                    ) : null}
                                    {selectedDesignType === 'StraightLine' ? (
                                        <>
                                            <label className="template-design-param">
                                                <span>Color</span>
                                                <input
                                                    type="color"
                                                    value={selectedLineColor}
                                                    onChange={handleSelectedLineColorChange}
                                                />
                                            </label>
                                            <label className="template-design-param">
                                                <span>Grosor</span>
                                                <input
                                                    type="number"
                                                    min="1"
                                                    max="24"
                                                    value={selectedLineStroke}
                                                    onChange={handleSelectedLineStrokeChange}
                                                />
                                            </label>
                                        </>
                                    ) : null}
                                    {selectedDesignType === 'SymbolDrawer' ? (
                                        <label className="template-design-param template-design-param--wide">
                                            <span>Ancho</span>
                                            <input
                                                type="range"
                                                min="180"
                                                max="1600"
                                                value={selectedDrawerWidth}
                                                onChange={handleSelectedDrawerWidthChange}
                                            />
                                        </label>
                                    ) : null}
                                </div>
                            ) : null}
                            <div className="template-action-group">
                                <div className="template-block-list">
                                    {templateBlocks.length === 0 ? (
                                        <div className="template-block-empty">
                                            Agrega bloques de imagen o texto para construir tu plantilla.
                                        </div>
                                    ) : (
                                        <div className="template-block-list-items">
                                            {templateBlocks.map((block, index) => {
                                                const isSelected = block.id === selectedTemplateBlockId;
                                                const label = block.type === 'image'
                                                    ? (block.image?.name || 'Imagen sin contenido')
                                                    : (getPlainText(block.text || '').trim() || 'Texto sin contenido');
                                                return (
                                                    <div
                                                        key={block.id}
                                                        className={`template-block-item${isSelected ? ' is-selected' : ''}`}
                                                        draggable
                                                        onDragStart={(event) => handleBlockDragStart(event, index)}
                                                        onDragOver={handleBlockDragOver}
                                                        onDrop={(event) => handleBlockDrop(event, index)}
                                                        onDragEnd={handleBlockDragEnd}
                                                        onClick={() => handleSelectBlock(block.id)}
                                                        tabIndex={0}
                                                        onKeyDown={(event) => {
                                                            if (event.key === 'Enter' || event.key === ' ') {
                                                                event.preventDefault();
                                                                handleSelectBlock(block.id);
                                                            }
                                                            if ((event.key === 'Delete' || event.key === 'Backspace') && isSelected) {
                                                                event.preventDefault();
                                                                handleRemoveBlock(block.id);
                                                            }
                                                        }}
                                                    >
                                                        <div className="template-block-item__info">
                                                            <span className="template-block-item__icon" aria-hidden="true">
                                                                {block.type === 'image' ? '🖼' : 'Aa'}
                                                            </span>
                                                            <span className="template-block-item__label">{label}</span>
                                                        </div>
                                                        <button
                                                            type="button"
                                                            className="template-block-item__remove"
                                                            onClick={(event) => {
                                                                event.stopPropagation();
                                                                handleRemoveBlock(block.id);
                                                            }}
                                                            aria-label="Eliminar bloque"
                                                        >
                                                            ×
                                                        </button>
                                                    </div>
                                                );
                                            })}
                                            <div
                                                className="template-block-dropzone"
                                                onDragOver={handleBlockDragOver}
                                                onDrop={(event) => handleBlockDrop(event, templateBlocks.length)}
                                                aria-hidden="true"
                                            />
                                        </div>
                                    )}
                                </div>
                                {selectedTemplateBlock?.type === 'text' ? (
                                    <div className="template-notes-section">
                                        <div className="template-notes">
                                            <div className="template-notes-toolbar">
                                                <button
                                                    type="button"
                                                    className={`template-notes-toolbar-button${noteBoldActive ? ' is-active' : ''}`}
                                                    onMouseDown={(event) => {
                                                        event.preventDefault();
                                                        handleNoteToolbarAction('bold');
                                                    }}
                                                    disabled={!isTemplateActive}
                                                    aria-pressed={noteBoldActive}
                                                    aria-label="Bold"
                                                    title="Bold"
                                                >
                                                    <span className="template-notes-toolbar-icon template-notes-toolbar-icon--bold">B</span>
                                                </button>
                                                <button
                                                    type="button"
                                                    className="template-notes-toolbar-button"
                                                    onMouseDown={(event) => event.preventDefault()}
                                                    onClick={() => handleNoteToolbarAction('increase')}
                                                    disabled={!isTemplateActive}
                                                    aria-label="Aumentar tamaño"
                                                    title="Aumentar tamaño"
                                                >
                                                    <span className="template-notes-toolbar-icon">A+</span>
                                                </button>
                                                <button
                                                    type="button"
                                                    className="template-notes-toolbar-button"
                                                    onMouseDown={(event) => event.preventDefault()}
                                                    onClick={() => handleNoteToolbarAction('decrease')}
                                                    disabled={!isTemplateActive}
                                                    aria-label="Reducir tamaño"
                                                    title="Reducir tamaño"
                                                >
                                                    <span className="template-notes-toolbar-icon">A-</span>
                                                </button>
                                            </div>
                                            <div
                                                className="template-notes-editor"
                                                ref={noteEditorRef}
                                                contentEditable={isTemplateActive}
                                                suppressContentEditableWarning
                                                data-placeholder="Escribir texto que aparecerá en la plantilla"
                                                onBeforeInput={handleNoteBeforeInput}
                                                onInput={handleNoteInput}
                                                onBlur={handleNoteBlur}
                                                onKeyUp={updateNoteBoldState}
                                                onMouseUp={updateNoteBoldState}
                                                tabIndex={isTemplateActive ? 0 : -1}
                                                aria-label="Texto de la plantilla"
                                            />
                                            <span className="template-notes-counter">{noteCharacterCount}/{NOTE_MAX_LENGTH}</span>
                                        </div>
                                        <div className="template-notes-side-actions">
                                            <button
                                                type="button"
                                                className="template-media-trash-button template-media-duplicate-button"
                                                onClick={() => handleDuplicateBlock(selectedTemplateBlock.id)}
                                                disabled={!isTemplateActive}
                                                aria-label="Clonar bloque de texto"
                                                title="Clonar bloque de texto"
                                            >
                                                <DuplicateIcon />
                                            </button>
                                            <button
                                                type="button"
                                                className="template-media-trash-button template-notes-trash-button"
                                                onClick={() => handleRemoveBlock(selectedTemplateBlock.id)}
                                                disabled={!isTemplateActive}
                                                aria-label="Eliminar bloque de texto"
                                                title="Eliminar bloque de texto"
                                            >
                                                <TrashIcon />
                                            </button>
                                        </div>
                                    </div>
                                ) : null}
                                {selectedTemplateBlock?.type === 'image' ? (
                                    <div className="template-media-actions">
                                        {selectedTemplateBlock.image ? (
                                            <>
                                                <div className="template-media-controls">
                                                    <div className="template-media-align-row">

                                                        <div className="template-media-ratio-control" role="group" aria-label="Relación de aspecto de la imagen">
                                                            <div className="template-media-ratio-row">
                                                                <button
                                                                    type="button"
                                                                    className="template-media-add-button"
                                                                    onClick={handleImageUploadTrigger}
                                                                    disabled={!isTemplateActive || isImageUploading}
                                                                    aria-label="Reemplazar imagen"
                                                                    title={isImageUploading ? 'Subiendo imagen' : 'Reemplazar imagen'}
                                                                >
                                                                    <AddImageIcon />
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    className={`template-media-ratio-button${selectedImageRatio === 'rectangle' ? ' is-active' : ''}`}
                                                                    onClick={handleImageRatioToggle}
                                                                    disabled={!isTemplateActive || isImageUploading}
                                                                    aria-label={selectedImageRatio === 'rectangle' ? 'Cambiar a cuadrada' : 'Cambiar a rectangular'}
                                                                >
                                                                    <RatioIcon variant={selectedImageRatio} />
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    className="template-media-trash-button"
                                                                    onClick={() => {
                                                                        handleRemoveBlock(selectedTemplateBlock.id);
                                                                        setImageUploadError('');
                                                                    }}
                                                                    disabled={!isTemplateActive || isImageUploading}
                                                                    aria-label="Eliminar bloque de imagen"
                                                                    title="Eliminar bloque de imagen"
                                                                >
                                                                    <TrashIcon />
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    {imageUploadError ? (
                                                        <div className="template-media-error template-media-inline-error" role="alert">
                                                            {imageUploadError}
                                                        </div>
                                                    ) : null}

                                                </div>
                                            </>
                                        ) : (
                                            <>
                                                {imageUploadError ? (
                                                    <div className="template-media-error template-media-inline-error" role="alert">
                                                        {imageUploadError}
                                                    </div>
                                                ) : null}
                                                <div className="template-media-empty-actions">
                                                    <button
                                                        type="button"
                                                        className="template-media-add-button"
                                                        onClick={handleImageUploadTrigger}
                                                        disabled={!isTemplateActive || isImageUploading}
                                                        aria-label="Agregar imagen"
                                                        title={isImageUploading ? 'Subiendo imagen' : 'Agregar imagen'}
                                                    >
                                                        <AddImageIcon />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="template-media-trash-button"
                                                        onClick={() => handleRemoveBlock(selectedTemplateBlock.id)}
                                                        disabled={!isTemplateActive || isImageUploading}
                                                        aria-label="Eliminar bloque de imagen"
                                                        title="Eliminar bloque de imagen"
                                                    >
                                                        <TrashIcon />
                                                    </button>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                ) : null}
                            </div>
                        </div>
                    </div>
                </div>
            ) : null}

            <input
                ref={imageUploadInputRef}
                type="file"
                accept={IMAGE_FILE_ACCEPT}
                onChange={handleImageFileInputChange}
                style={{ display: 'none' }}
            />

        </nav>
        </div>
    </div>
    );
}

export default MenuBar;
